// The record as the live canvas's own components read it (MAJLIS-67, UI-S3, UI-C3, UI-C4, UI-C5). A finished majlis is
// the live room's bubbles with the room taken away: every phrase is a LANDED bubble (P-21 carries final phrases only,
// D-17), with its stored clip's length for Replay, and its seat coloured by join order exactly as the live room colours
// it. Ruling 10: nobody is "me" in a record - the host's phrases are named like everyone's - so the Bubble is always
// given isMe=false. Pure functions: no clock, no fetch.
import type { Bubble, SeatView, TranslationView } from "../room/model";
import { SEAT_COLOURS } from "../room/model";
import type { RecordData, RecordPhrase, RecordSeat } from "../protocol/records";

export interface RecordView {
  seats: Record<string, SeatView>;
  bubbles: Bubble[];
  phrases: Record<string, RecordPhrase>;
}

/** A record seat as the Bubble reads a SeatView; its colour is its join-order slot, as live (UI-C8). */
export function seatView(s: RecordSeat, order: number): SeatView {
  return {
    id: s.id,
    name: s.name,
    kind: s.kind,
    speak_lang: s.speak_lang,
    read_lang: "",
    muted: false,
    listening: false,
    colour: order % SEAT_COLOURS,
    present: false,
  };
}

export function recordView(r: RecordData): RecordView {
  const seats: Record<string, SeatView> = {};
  r.seats.forEach((s, i) => (seats[s.id] = seatView(s, i)));
  const phrases: Record<string, RecordPhrase> = {};
  const bubbles = r.phrases.map((p): Bubble => {
    phrases[p.segment.id] = p;
    return {
      id: p.segment.id,
      seat: p.segment.seat,
      t0: p.segment.t0,
      phase: "landed",
      startedAt: 0,
      endedAt: 0,
      segment: p.segment,
      revised: false,
      revisionCount: 0,
      hadLive: false,
      wall: Date.parse(p.at),
      audioMs: p.audio_ms ?? undefined,
    };
  });
  return { seats, bubbles, phrases };
}

/** What sits under a phrase's original: its translation, the pending dots while one is on its way (and the reader has
 * not given up waiting), or nothing - the reader's own language, or a translation that will not come. */
export function recordTranslation(p: RecordPhrase | undefined, waiting: boolean): TranslationView {
  if (!p) return { kind: "none" };
  if (p.translation) return { kind: "text", lang: p.translation.lang, text: p.translation.text };
  return p.pending && waiting ? { kind: "pending" } : { kind: "none" };
}

/** How many phrases carry a translation: the progress a re-read is judged by (RECORD_POLL_IDLE_MAX). */
export function translatedCount(r: RecordData): number {
  return r.phrases.reduce((n, p) => n + (p.translation ? 1 : 0), 0);
}

export function anyPending(r: RecordData): boolean {
  return r.phrases.some((p) => p.pending);
}
